import { AppIdMetricsOverview, AppIdMetricsDetail, TableCountsResponse } from '../../../api/types';
import { isDisplayableAppMetricVersion } from '../../../utils/appVersionDisplay';

/** Dashboard versiyon tabloları ve çizgi grafiklerinde gösterilen toplam sürüm sayısı. */
export const DASHBOARD_TOP_VERSION_COUNT = 6;

/** Platform başına gösterilen sürüm (5.0 Android + 5.1 iOS). */
export const DASHBOARD_VERSIONS_PER_PLATFORM = 3;

/** Android uygulama hattı (major.minor). */
export const DASHBOARD_ANDROID_BRANCH = '5.0';

/** iOS uygulama hattı (major.minor). */
export const DASHBOARD_IOS_BRANCH = '5.1';

/** Takvim “bugün” sayısı; eski API’de yalnızca past_24h varsa o değer burada gösterilir. */
export function getOverviewTodayDisplay(item: AppIdMetricsOverview): number {
  if (typeof item.today === 'number') return item.today;
  if (typeof item.past_24h === 'number') return item.past_24h;
  return 0;
}

/** Takvim “dün” sayısı; eski API’de ayrı alan olmadığından 0. */
export function getOverviewYesterdayDisplay(item: AppIdMetricsOverview): number {
  if (typeof item.yesterday === 'number') return item.yesterday;
  return 0;
}

/** Son hafta sütunu: eski API’de past_7d; yeni API’de last_week. */
export function getOverviewLastWeekDisplay(item: AppIdMetricsOverview): number {
  if (typeof item.past_7d === 'number') return item.past_7d;
  return item.last_week ?? 0;
}

export interface VersionStatRow {
  version: string;
  today: number;
  yesterday: number;
  lastWeek: number;
  total: number;
}

function getVersionBranch(version: string): string | null {
  const normalized = (version ?? '').trim().replace(/^v/i, '');
  if (!normalized) return null;
  const parts = normalized.split('.');
  if (parts.length < 2) return null;
  if (!parts.every((p) => /^\d+$/.test(p))) return null;
  return `${parts[0]}.${parts[1]}`;
}

export function compareVersions(a: string, b: string): number {
  const parseVersion = (v: string): number[] => v.split('.').map(num => parseInt(num, 10) || 0);
  const aParts = parseVersion(a);
  const bParts = parseVersion(b);
  const maxLength = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < maxLength; i++) {
    const aPart = aParts[i] || 0;
    const bPart = bParts[i] || 0;
    if (aPart !== bPart) return aPart - bPart;
  }
  return 0;
}

export function processVersionStats(
  overviewMetrics: AppIdMetricsOverview[],
  metricType: 'app_session' | 'game_session' | 'test_session' | 'chat_session' | 'users' | 'advertisements'
): VersionStatRow[] {
  const filtered = overviewMetrics.filter(
    m => m.metric_type === metricType && isDisplayableAppMetricVersion(m.app_id ?? '')
  );
  const stats = filtered.map(item => ({
    version: item.app_id,
    today: getOverviewTodayDisplay(item),
    yesterday: getOverviewYesterdayDisplay(item),
    lastWeek: getOverviewLastWeekDisplay(item),
    total: item.total ?? 0,
  }));
  return stats.sort((a, b) => compareVersions(b.version, a.version));
}

/**
 * Dashboard için dengeli platform seçimi: 3× 5.1 (iOS) + 3× 5.0 (Android).
 * stats en yeniden eskiye sıralı olmalı; her hattan ilk N sürüm alınır.
 */
export function pickBranchDiverseTopVersions(
  stats: VersionStatRow[],
  limit = DASHBOARD_TOP_VERSION_COUNT
): VersionStatRow[] {
  if (limit <= 0) return [];

  const perPlatform = Math.min(
    DASHBOARD_VERSIONS_PER_PLATFORM,
    Math.max(1, Math.floor(limit / 2))
  );

  const ios = stats
    .filter((row) => getVersionBranch(row.version) === DASHBOARD_IOS_BRANCH)
    .slice(0, perPlatform);
  const android = stats
    .filter((row) => getVersionBranch(row.version) === DASHBOARD_ANDROID_BRANCH)
    .slice(0, perPlatform);

  const picked: VersionStatRow[] = [...ios, ...android];
  const pickedVersions = new Set(picked.map((p) => p.version));

  if (picked.length < limit) {
    for (const row of stats) {
      if (picked.length >= limit) break;
      if (!pickedVersions.has(row.version)) {
        picked.push(row);
        pickedVersions.add(row.version);
      }
    }
  }

  return picked.sort((a, b) => compareVersions(b.version, a.version));
}

export function processLineChartData(
  metrics: AppIdMetricsDetail[],
  top3Versions: string[]
): Record<string, unknown>[] {
  const scoped = metrics.filter((row) => isDisplayableAppMetricVersion(row.app_id));
  if (scoped.length === 0) return [];
  const dateGroups = scoped.reduce((acc, item) => {
    if (!acc[item.session_date]) acc[item.session_date] = {};
    acc[item.session_date][item.app_id] = item.daily_sessions_count;
    return acc;
  }, {} as Record<string, Record<string, number>>);
  const dates = Object.keys(dateGroups).sort();
  const last30Days = dates.slice(-30);
  return last30Days.map(date => {
    const dataPoint: Record<string, unknown> = {
      name: new Date(date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }),
      date,
    };
    const dateRow = dateGroups[date] || {};
    top3Versions.forEach(version => {
      dataPoint[version] = dateRow[version] || 0;
    });
    const allVersionsTotal = Object.entries(dateRow)
      .filter(([appId]) => isDisplayableAppMetricVersion(appId))
      .reduce((sum, [, count]) => sum + count, 0);
    dataPoint['Tüm Versiyonlar'] = allVersionsTotal > 0 ? allVersionsTotal : undefined;
    return dataPoint;
  });
}

export function processUserLineChartData(
  metrics: AppIdMetricsDetail[],
  topVersions: VersionStatRow[]
): Record<string, unknown>[] {
  const scoped = metrics.filter((row) => isDisplayableAppMetricVersion(row.app_id));
  if (scoped.length === 0) return [];
  const topVersionIds = topVersions.map(stat => stat.version);

  const dateGroups = scoped.reduce((acc, item) => {
    if (!acc[item.session_date]) acc[item.session_date] = {};
    if (!acc[item.session_date][item.app_id]) acc[item.session_date][item.app_id] = 0;
    acc[item.session_date][item.app_id] += item.daily_sessions_count;
    return acc;
  }, {} as Record<string, Record<string, number>>);
  const dates = Object.keys(dateGroups).sort();
  const last30Days = dates.slice(-30);
  return last30Days.map(date => {
    const dataPoint: Record<string, unknown> = {
      name: new Date(date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }),
      date,
    };
    const dateRow = dateGroups[date] || {};
    topVersionIds.forEach((version, index) => {
      const count = dateRow[version] || 0;
      dataPoint[`Seri${index + 1}`] = count;
      dataPoint[`version${index + 1}`] = version;
    });
    const allVersionsTotal = Object.entries(dateRow)
      .filter(([appId]) => isDisplayableAppMetricVersion(appId))
      .reduce((sum, [, count]) => sum + count, 0);
    dataPoint['Tüm Versiyonlar'] = allVersionsTotal > 0 ? allVersionsTotal : undefined;
    return dataPoint;
  });
}

export function processBarChartDataForMetric(
  overviewMetrics: AppIdMetricsOverview[],
  metricType: 'test_session' | 'chat_session'
) {
  const stats = processVersionStats(overviewMetrics, metricType);
  return stats
    .sort((a, b) => b.total - a.total)
    .slice(0, 10)
    .map(stat => ({
      name: stat.version.length > 8 ? stat.version.substring(0, 8) + '...' : stat.version,
      value: stat.total,
      fullName: stat.version,
    }));
}

export function filterChartData(
  chartData: Record<string, unknown>[],
  top3Versions: string[],
  isUserChart = false
): Record<string, unknown>[] {
  return chartData.filter(dataPoint => {
    let hasData = false;
    if (isUserChart) {
      hasData = top3Versions.some((version, index) => {
        const seriesKey = `Seri${index + 1}`;
        return ((dataPoint[seriesKey] as number) || 0) > 0;
      });
    } else {
      hasData = top3Versions.some(version => ((dataPoint[version] as number) || 0) > 0);
    }
    const tumVersionlarValue = dataPoint['Tüm Versiyonlar'] as number | undefined;
    const hasTumVersionlar =
      tumVersionlarValue != null && tumVersionlarValue !== undefined && tumVersionlarValue > 0;
    return hasData || hasTumVersionlar;
  });
}

export function getDailyChange(tableCounts: TableCountsResponse | null, metricType: string): string {
  if (!tableCounts?.metrics) return '+0%';
  const key = `${metricType}_daily`;
  const metric = tableCounts.metrics[key];
  if (!metric) return '+0%';
  const change = metric.growth_percentage;
  return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
}

export function getWeeklyChange(tableCounts: TableCountsResponse | null, metricType: string): string {
  if (!tableCounts?.metrics) return '+0%';
  const key = `${metricType}_weekly`;
  const metric = tableCounts.metrics[key];
  if (!metric) return '+0%';
  const change = metric.growth_percentage;
  return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
}

export function getMonthlyChange(tableCounts: TableCountsResponse | null, metricType: string): string {
  if (!tableCounts?.metrics) return '+0%';
  const key = `${metricType}_monthly`;
  const metric = tableCounts.metrics[key];
  if (!metric) return '+0%';
  const change = metric.growth_percentage;
  return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
}

export function getTotalCount(tableCounts: TableCountsResponse | null, metricType: string): number {
  if (!tableCounts) return 0;
  const count = tableCounts.total_counts.find(tc => tc.metric_type === metricType);
  return count?.total || 0;
}
